"use client";

import { useEffect } from "react";
import {
  POSTHOG_READY_EVENT,
  registerExperimentArm,
  trackExperimentExposure,
} from "@/lib/analytics";
import { onConsentChange } from "@/lib/consent";
import { site } from "@/lib/site";

// Stable id for the old-Framer-vs-new-Next landing-page test.
const EXPERIMENT_ID = "landing-redesign";

function readCookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];
}

// Which arm of the landing-page test this page load belongs to, or undefined if
// the page is not part of the test at all.
//
// The test ships in two phases and the arm is carried differently in each:
//
//   Phase 1 (live now) is a Google Ads experiment. There is no edge Worker and
//   so no cookie: the ad platform splits the traffic, and THE ARM IS THE HOST.
//   Reading the cookie alone meant this component reported nothing at all for
//   the whole of phase 1 - the new landing page had no arm label on any event,
//   which is the one thing the experiment needs to be readable.
//
//   Phase 2 is the same-URL Cloudflare Worker split on the apex. There the host
//   says nothing (both arms answer on trein-vertraging.nl) and the Worker's
//   `ab_variant` cookie is the only source of truth.
//
// THE HOST WINS, and the order matters. The Worker sets `ab_variant` on the
// parent domain (`Domain=.trein-vertraging.nl`, 30 days - see
// infra/cloudflare/ab-test/worker/src/index.js), so an assignment made on the
// apex is sent to start.trein-vertraging.nl too. Checking the cookie first
// meant a visitor bucketed `control` on the apex who later clicked a Google Ads
// link to the paid host was recorded as control while looking at the variant
// page. The host is evidence of what is actually being served; the cookie is
// only a record of what some other host decided, and it outlives its scope.
export function resolveVariant(hostname: string, cookie?: string): string | undefined {
  if (hostname.toLowerCase() === site.hosts.paidVariant) return "variant";
  if (cookie === "control" || cookie === "variant") return cookie;
  return undefined;
}

// Reports which arm of the landing-page test this visitor is in, once per page
// load. Renders nothing. The control (Framer) arm is measured on its own side,
// so an event from here always means the visitor is on the new page.
//
// The first-party and GTM legs go out immediately. The PostHog leg cannot: it
// needs posthog.init(), which PostHogProvider only runs once OneTrust grants
// analytics consent, and that is always after this mounts. So it is retried
// when PostHog announces itself and on any later consent change, exactly once.
// Without that retry the `ab_variant` super property was never set and no
// PostHog event was ever segmented by arm.
export function ExperimentExposure() {
  useEffect(() => {
    const variant = resolveVariant(
      window.location.hostname,
      readCookie("ab_variant"),
    );
    if (!variant) return;

    trackExperimentExposure(EXPERIMENT_ID, variant);

    // Already loaded (repeat visitor with consent stored): nothing to wait for.
    if (registerExperimentArm(EXPERIMENT_ID, variant)) return;

    let delivered = false;
    const attempt = () => {
      if (delivered) return;
      if (registerExperimentArm(EXPERIMENT_ID, variant)) {
        delivered = true;
        stopWaiting();
      }
    };

    // Both hooks, deliberately. The consent event can land before
    // PostHogProvider has finished init(), in which case the attempt it
    // triggers fails and the ready event is what delivers.
    window.addEventListener(POSTHOG_READY_EVENT, attempt);
    const unsubscribeConsent = onConsentChange(attempt);
    function stopWaiting() {
      window.removeEventListener(POSTHOG_READY_EVENT, attempt);
      unsubscribeConsent();
    }

    return stopWaiting;
  }, []);
  return null;
}
